import { HttpHeaders, HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RuntimeConfigService } from "../../../services/runtime-config.service";
import { SparkClassDto } from "./models/SparkClassDto";
import { Observable } from "rxjs";
import { SparkStudentDto } from "./models/SparkStudentDto";
import { SparkGroup } from "./models/SparkGroup";
import { SparkGroupSet, SparkClassroomGroups } from "./models/SparkGroupSet";
import { SparkClassSettings, SaveSparkClassSettingsRequest, SparkCustomBehavior, SparkSchoolBehaviors, SparkSchoolBehaviorPoint } from "./models/SparkClassSettings";
import { SparkGroupAward, AwardPointsToGroupRequest } from "./models/SparkGroupAward";
import { SparkClassGoal, SaveSparkClassGoalRequest } from "./models/SparkClassGoal";
import { SparkLiveState, SparkLiveAwards, QuickAwardGroupResult } from "./models/SparkLive";
import { SparkCategory } from "./models/SparkCategory";
import { School } from "../../models/school.model";
import { GuardianStudent } from "../profiles/models/guardian-student.model";
import { Reward } from "./models/rewards/Reward";
import { PaginationModel } from "../shared/pagination-model";
import { RewardsPointAllocation } from "./models/rewards/RewardsPointAllocation";
import { RewardsPointTransaction } from "./models/rewards/RewardsPointTransaction";
import { SchoolRewardsPointTransaction } from "./models/school-rewards/SchoolRewardsPointTransaction";
import { SchoolRewardsHistoryItem } from "./models/school-rewards/SchoolRewardsHistoryItem";
import { SchoolRewardsPurchase } from "./models/school-rewards/SchoolRewardsPurchase";
import { SchoolReward } from "./models/school-rewards/SchoolReward";
import { SchoolStateGroup } from "../principal-task-mgt/models/SchoolStateGroup";
import { Staff } from "./models/rewards/Staff";
import { Student } from "../student/models/Student";
import { SchoolRewardsConfig } from "./models/school-rewards/SchoolRewardsConfig";
import { StaffBehaviorReportItem } from "./models/school-rewards/StaffBehaviorReportItem";
import { StudentBehaviorReportItem } from "./models/school-rewards/StudentBehaviorReportItem";
import { StudentBehaviorEventItem } from "./models/school-rewards/StudentBehaviorEventItem";
import { StaffGradeReportItem } from "./models/school-rewards/StaffGradeReportItem";
import { StaffBehaviorEventItem } from "./models/school-rewards/StaffBehaviorEventItem";
import { TeacherEngagementReportItem } from "./models/school-rewards/TeacherEngagementReportItem";
import { ParentEngagementReportItem } from "./models/school-rewards/ParentEngagementReportItem";
import { SchoolRewardsRaffle } from "./models/school-rewards/SchoolRewardsRaffle";
import { SchoolRewardsRaffleEntry } from "./models/school-rewards/SchoolRewardsRaffleEntry";
import { SparkRaffle } from "./models/SparkRaffle";
import { SparkRaffleEntry } from "./models/SparkRaffleEntry";
import { StudentListItemDto } from "./models/StudentListItemDto";
import { SparkPointAwardLogDto } from "./models/SparkPointAwardLogDto";
import { SparkCategoryCustom, SparkCategoryCustomPointsType } from "./models/SparkCategoryCustom";
import { TeacherStudentPointSummaryItem } from "./models/school-rewards/TeacherStudentPointSummaryItem";
import { TeacherPointsAwardedItem } from "./models/school-rewards/TeacherPointsAwardedItem";
import { AcademicYearDateRange } from "./models/school-rewards/AcademicYearDateRange";

@Injectable({
  providedIn: 'root'
})
export class SparkService {
  private apiUrl: string;
  private baseApiUrl: string;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient, private runtimeConfig: RuntimeConfigService) {
    this.baseApiUrl = this.runtimeConfig.currentConfig.apiUrl;
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/Spark';

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.baseApiUrl = config.apiUrl;
      this.apiUrl = config.apiUrl + '/Spark';
    });
  }

  addOrUpdateSparkGroup(sparkGroup: SparkGroup): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/AddOrUpdateSparkGroupAsync/`, sparkGroup);
  }

  awardParentPointsToStudent(schoolId: number, academicYear: number, studentId: number, guardianId: number, points: number, reason: string, notes?: string): Observable<boolean> {
    const body = {
      ...(schoolId && { schoolId }),
      ...(academicYear && { academicYear }),
      ...(studentId && { studentId }),
      ...(guardianId && { guardianId }),
      ...(points && { points }),
      ...(reason && { reason }),
      ...(notes !== null && notes !== undefined && { notes })
    };

    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/AwardParentPointsToStudentAsync`, body);
  }

  awardSchoolPointsToStudents(studentIds: number[], teacherId: number, schoolId: number, academicYear: number, points: number, reason: string, notes?: string): Observable<boolean> {
    const body = {
      ...(studentIds && { studentIds }),
      ...(teacherId && { teacherId }),
      ...(schoolId && { schoolId }),
      ...(academicYear && { academicYear }),
      ...(points && { points }),
      ...(reason && { reason }),
      ...(notes !== null && notes !== undefined && { notes })
    };

    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/AwardPointsToStudentsAsync`, body);
  }

  deleteGroup(sparkGroupId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/DeleteSparkGroupAsync/`, sparkGroupId);
  }

  deleteSchoolReward(rewardId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/DeleteRewardAsync/${rewardId}`, {});
  }

  getAllSchools(): Observable<School[]> {
    return this.http.get<School[]>(this.baseApiUrl + "/School/GetCurrentSchoolsAsync/");
  }

  // Schools the SPARK school admin screen may work with: every school for SparkAdmin, otherwise the
  // user's own staff schools (for a DSQ, their assigned schools). Never includes the Service Center.
  getManageableSchools(): Observable<School[]> {
    return this.http.get<School[]>(`${this.baseApiUrl}/SchoolRewards/GetManageableSchoolsAsync`);
  }

  getAvailableParentPointsForStudentId(studentId: number): Observable<number> {
    return this.http.get<number>(`${this.baseApiUrl}/SchoolRewards/GetAvailableParentPointsForStudentIdAsync/${studentId}`);
  }

  getClassesForStudent(studentId: number): Observable<SparkClassDto[]> {
    return this.http.get<SparkClassDto[]>(`${this.apiUrl}/GetSparkClassesForStudentAsync/${studentId}`);
  }

  getClassesForTeacher(academicYear: number, schoolId: number): Observable<SparkClassDto[]> {
    return this.http.get<SparkClassDto[]>(`${this.apiUrl}/GetSparkClassesForTeacherAsync/${academicYear}/${schoolId}`);
  }

  // The teacher's schools that have SPARK enabled — populates the spark-teacher school selector.
  getSparkEnabledSchoolsForTeacher(): Observable<School[]> {
    return this.http.get<School[]>(`${this.baseApiUrl}/SchoolRewards/GetSparkEnabledSchoolsForTeacherAsync`);
  }

  getGuardianStudentDtoByGuardianId(guardianId: number): Observable<GuardianStudent[]> {
    return this.http.get<GuardianStudent[]>(`${this.baseApiUrl}/Guardian/GetGuardianStudentDtoByGuardianIdAsync/${guardianId}`);
  }

  getSchoolPointAllocationsForTeacherByAcademicYear(schoolId: number, teacherId: number, academicYear: number, pageNumber: number, pageSize: number): Observable<PaginationModel<RewardsPointAllocation>> {
    let params: HttpParams = new HttpParams();
    params = params.set('schoolId', schoolId.toString());
    params = params.set('teacherId', teacherId.toString());
    params = params.set('academicYear', academicYear.toString());
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginationModel<RewardsPointAllocation>>(`${this.baseApiUrl}/SchoolRewards/GetPointAllocationsForTeacherByAcademicYearAsync`, {
      params
    });
  }

  getPointTransactionsForStudent(studentId: number, schoolId: number, academicYear: number, pageNumber: number, pageSize: number): Observable<PaginationModel<SchoolRewardsPointTransaction>> {
    return this.http.get<PaginationModel<SchoolRewardsPointTransaction>>(
      `${this.baseApiUrl}/SchoolRewards/GetPointTransactionsForStudentAsync/${studentId}/${schoolId}/${academicYear}/${pageNumber}/${pageSize}`);
  }

  // Unified points history: earned + spent (store purchases and raffle tickets), across all years, paged.
  getStudentPointsHistory(studentId: number, schoolId: number, pageNumber: number, pageSize: number): Observable<PaginationModel<SchoolRewardsHistoryItem>> {
    return this.http.get<PaginationModel<SchoolRewardsHistoryItem>>(
      `${this.baseApiUrl}/SchoolRewards/GetStudentPointsHistoryAsync/${studentId}/${schoolId}/${pageNumber}/${pageSize}`);
  }

  getSchoolPointTransactionsForTeacherByAcademicYear(teacherId: number, schoolId: number, academicYear: number, pageNumber: number, pageSize: number): Observable<PaginationModel<RewardsPointTransaction>> {
    let params: HttpParams = new HttpParams();
    params = params.set('teacherId', teacherId.toString());
    params = params.set('schoolId', schoolId.toString());
    params = params.set('academicYear', academicYear.toString());
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginationModel<RewardsPointTransaction>>(`${this.baseApiUrl}/SchoolRewards/GetPointTransactionsForTeacherByAcademicYearAsync`, {
      params
    });
  }

  getSchoolForStudent(academicYear: number, studentId: number): Observable<School> {
    return this.http.get<School>(`${this.baseApiUrl}/School/GetSchoolForStudentAsync/${academicYear}/${studentId}`);
  }

  getSchoolGroupsByState(includeServiceCenter: boolean): Observable<SchoolStateGroup[]> {
    return this.http.get<SchoolStateGroup[]>(`${this.baseApiUrl}/School/GetSchoolsWithStateAsync/${includeServiceCenter}`);
  }

  getSparkCategories(): Observable<SparkCategory[]> {
    return this.http.get<SparkCategory[]>(`${this.apiUrl}/GetSparkCategoriesAsync`);
  }

  getCustomCategories(): Observable<SparkCategoryCustom[]> {
    return this.http.get<SparkCategoryCustom[]>(`${this.apiUrl}/GetCustomCategoriesAsync`);
  }

  saveCustomCategory(description: string, pointsType: SparkCategoryCustomPointsType, points: number): Observable<SparkCategoryCustom> {
    return this.http.post<SparkCategoryCustom>(`${this.apiUrl}/SaveCustomCategoryAsync`, {
      description,
      pointsType,
      points
    });
  }

  deleteCustomCategory(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/DeleteCustomCategoryAsync/${id}`);
  }

  // ---- Group awards ----
  awardPointsToGroup(request: AwardPointsToGroupRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/AwardPointsToGroupAsync`, request);
  }

  getSparkGroupAwards(sectionId: number): Observable<SparkGroupAward[]> {
    return this.http.get<SparkGroupAward[]>(`${this.baseApiUrl}/SchoolRewards/GetSparkGroupAwardsAsync/${sectionId}`);
  }

  // ---- Class settings ----
  getSparkClassSettings(sectionId: number): Observable<SparkClassSettings> {
    return this.http.get<SparkClassSettings>(`${this.apiUrl}/GetSparkClassSettingsAsync/${sectionId}`);
  }

  saveSparkClassSettings(request: SaveSparkClassSettingsRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/SaveSparkClassSettingsAsync`, request);
  }

  saveSparkCustomBehavior(behavior: SparkCustomBehavior): Observable<SparkCustomBehavior> {
    return this.http.post<SparkCustomBehavior>(`${this.apiUrl}/SaveSparkCustomBehaviorAsync`, behavior);
  }

  deleteSparkCustomBehavior(id: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/DeleteSparkCustomBehaviorAsync/${id}`, {});
  }

  // School-level behavior defaults (admins).
  getSparkSchoolBehaviors(schoolId: number): Observable<SparkSchoolBehaviors> {
    return this.http.get<SparkSchoolBehaviors>(`${this.apiUrl}/GetSparkSchoolBehaviorsAsync/${schoolId}`);
  }

  saveSparkSchoolBehaviorPoint(request: SparkSchoolBehaviorPoint): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/SaveSparkSchoolBehaviorPointAsync`, request);
  }

  // Blank clears the nickname.
  saveSparkStudentNickname(studentId: number, nickname: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/SaveSparkStudentNicknameAsync`, { StudentId: studentId, Nickname: nickname });
  }

  // Class goal for Live view: the active goal with progress (null when none), start one, end one.
  getSparkClassGoal(sectionId: number): Observable<SparkClassGoal | null> {
    return this.http.get<SparkClassGoal | null>(`${this.apiUrl}/GetSparkClassGoalAsync/${sectionId}`);
  }

  saveSparkClassGoal(request: SaveSparkClassGoalRequest): Observable<SparkClassGoal> {
    return this.http.post<SparkClassGoal>(`${this.apiUrl}/SaveSparkClassGoalAsync`, request);
  }

  endSparkClassGoal(goalId: number, completed: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/EndSparkClassGoalAsync/${goalId}/${completed}`, null);
  }

  // Group sets and single groups the user built for one classroom.
  getSparkClassroomGroups(sectionId: number): Observable<SparkClassroomGroups> {
    return this.http.get<SparkClassroomGroups>(`${this.apiUrl}/GetSparkClassroomGroupsAsync/${sectionId}`);
  }

  // Creates or updates a group set with its groups and members; resolves to the set id.
  saveSparkGroupSet(set: SparkGroupSet): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/SaveSparkGroupSetAsync`, set);
  }

  deleteSparkGroupSet(setId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/DeleteSparkGroupSetAsync/${setId}`, {});
  }

  getSparkGroups(academicYear: number, schoolId: number): Observable<SparkGroup[]> {
    return this.http.get<SparkGroup[]>(`${this.apiUrl}/GetSparkGroupsAsync/${academicYear}/${schoolId}`);
  }

  getSparkStudent(studentId: number, academicYear: number): Observable<SparkStudentDto> {
    return this.http.get<SparkStudentDto>(`${this.apiUrl}/GetSparkStudentAsync/${studentId}/${academicYear}`);
  }

  getSparkStudentsForClass(sectionId: number): Observable<SparkStudentDto[]> {
    return this.http.get<SparkStudentDto[]>(`${this.apiUrl}/GetSparkStudentsForClassAsync/${sectionId}`);
  }

  getSparkStudentsForGroup(groupId: number): Observable<SparkStudentDto[]> {
    return this.http.get<SparkStudentDto[]>(`${this.apiUrl}/GetSparkStudentsForGroupAsync/${groupId}`);
  }

  getSparkStudentsForStudentClassRoster(studentId: number): Observable<SparkStudentDto[]> {
    return this.http.get<SparkStudentDto[]>(`${this.apiUrl}/GetSparkStudentsForStudentClassRosterAsync/${studentId}`);
  }

  getSparkStudentWishList(studentId: number): Observable<Reward[]> {
    return this.http.get<Reward[]>(`${this.apiUrl}/GetSparkStudentWishListAsync/${studentId}`);
  }

  getStaffBySchoolId(schoolId: number): Observable<Staff[]> {
    return this.http.get<Staff[]>(`${this.baseApiUrl}/Staff/GetStaffBySchoolIdAsync/${schoolId}`);
  }

  getStudentPurchaseHistory(studentId: number, schoolId: number, pageNumber: number, pageSize: number): Observable<PaginationModel<SchoolRewardsPurchase>> {
    return this.http.get<PaginationModel<SchoolRewardsPurchase>>(
      `${this.baseApiUrl}/SchoolRewards/GetStudentPurchaseHistoryAsync/${studentId}/${schoolId}/${pageNumber}/${pageSize}`);
  }

  getStudentsBySchoolId(schoolId: number): Observable<Student[]> {
    return this.http.get<Student[]>(`${this.baseApiUrl}/Student/GetStudentsBySchoolIdAsync/${schoolId}`);
  }

  getStudentListBySchoolId(schoolId: number, academicYear: number): Observable<StudentListItemDto[]> {
    return this.http.get<StudentListItemDto[]>(`${this.baseApiUrl}/Student/GetStudentListBySchoolIdAsync/${schoolId}/${academicYear}`);
  }

  // Ids of sections at the school whose roster has a student matching the name (classrooms search).
  getSectionIdsWithStudent(academicYear: number, schoolId: number, searchString: string): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiUrl}/GetSectionIdsWithStudentAsync/${academicYear}/${schoolId}?searchString=${encodeURIComponent(searchString)}`);
  }

  searchForSparkStudent(schoolId: number, academicYear: number, searchString: string): Observable<SparkStudentDto[]> {
    return this.http.get<SparkStudentDto[]>(`${this.apiUrl}/SearchForSparkStudentAsync/${schoolId}/${academicYear}?searchString=${encodeURIComponent(searchString)}`);
  }

  updateAllowNegativePoints(schoolId: number, allowNegativePoints: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/UpdateAllowNegativePointsAsync/${schoolId}/${allowNegativePoints}`, {});
  }

  updateClassFavoriteAsync(sectionId: number, isFavorite: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/UpdateClassFavoriteAsync/${sectionId}/${isFavorite}`, {});
  }

  updateGroupFavoriteAsync(groupId: number, isFavorite: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/UpdateGroupFavoriteAsync/${groupId}/${isFavorite}`, {});
  }

  // School Rewards Config
  getSchoolRewardsConfig(schoolId: number): Observable<SchoolRewardsConfig> {
    return this.http.get<SchoolRewardsConfig>(`${this.baseApiUrl}/SchoolRewards/GetConfigAsync/${schoolId}`);
  }

  getPositiveBehaviorByStaff(schoolId: number, academicYear: number): Observable<StaffBehaviorReportItem[]> {
    return this.http.get<StaffBehaviorReportItem[]>(`${this.baseApiUrl}/SchoolRewards/GetPositiveBehaviorByStaffAsync/${schoolId}/${academicYear}`);
  }

  getPositiveBehaviorByStudent(schoolId: number, academicYear: number): Observable<StudentBehaviorReportItem[]> {
    return this.http.get<StudentBehaviorReportItem[]>(`${this.baseApiUrl}/SchoolRewards/GetPositiveBehaviorByStudentAsync/${schoolId}/${academicYear}`);
  }

  getStudentBehaviorEvents(schoolId: number, studentId: number, academicYear: number, positiveOnly: boolean): Observable<StudentBehaviorEventItem[]> {
    return this.http.get<StudentBehaviorEventItem[]>(`${this.baseApiUrl}/SchoolRewards/GetStudentBehaviorEventsAsync/${schoolId}/${studentId}/${academicYear}/${positiveOnly}`);
  }

  getPositiveBehaviorByStaffGrade(schoolId: number, academicYear: number): Observable<StaffGradeReportItem[]> {
    return this.http.get<StaffGradeReportItem[]>(`${this.baseApiUrl}/SchoolRewards/GetPositiveBehaviorByStaffGradeAsync/${schoolId}/${academicYear}`);
  }

  getStaffBehaviorEventsByGrade(schoolId: number, userId: number, gradeLevelId: number, academicYear: number, positiveOnly: boolean): Observable<StaffBehaviorEventItem[]> {
    return this.http.get<StaffBehaviorEventItem[]>(`${this.baseApiUrl}/SchoolRewards/GetStaffBehaviorEventsByGradeAsync/${schoolId}/${userId}/${gradeLevelId}/${academicYear}/${positiveOnly}`);
  }

  getTeacherEngagement(schoolId: number, academicYear: number): Observable<TeacherEngagementReportItem[]> {
    return this.http.get<TeacherEngagementReportItem[]>(`${this.baseApiUrl}/SchoolRewards/GetTeacherEngagementAsync/${schoolId}/${academicYear}`);
  }

  getParentEngagement(schoolId: number, academicYear: number): Observable<ParentEngagementReportItem[]> {
    return this.http.get<ParentEngagementReportItem[]>(`${this.baseApiUrl}/SchoolRewards/GetParentEngagementAsync/${schoolId}/${academicYear}`);
  }

  // Teacher Reports "Student Summary": per-student earned/lost/net/balance for the selected class or
  // group over a date range. Pass the section id, or 0 with a group id; fromDate/toDate are
  // 'yyyy-MM-dd' strings (inclusive calendar days).
  getClassStudentPointSummary(schoolId: number, sectionId: number, groupId: number, fromDate: string, toDate: string): Observable<TeacherStudentPointSummaryItem[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('fromDate', fromDate);
    params = params.set('toDate', toDate);

    return this.http.get<TeacherStudentPointSummaryItem[]>(`${this.baseApiUrl}/SchoolRewards/GetClassStudentPointSummaryAsync/${schoolId}/${sectionId}/${groupId}`, {
      params
    });
  }

  // Teacher Reports "Points Awarded": each recognition given to a student in the selected class or
  // group over a date range, whoever awarded it.
  getClassPointsAwarded(schoolId: number, sectionId: number, groupId: number, fromDate: string, toDate: string): Observable<TeacherPointsAwardedItem[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('fromDate', fromDate);
    params = params.set('toDate', toDate);

    return this.http.get<TeacherPointsAwardedItem[]>(`${this.baseApiUrl}/SchoolRewards/GetClassPointsAwardedAsync/${schoolId}/${sectionId}/${groupId}`, {
      params
    });
  }

  // Edits an existing award. The server recomputes the student's balance from source, so a changed
  // point value needs no adjustment here; it also re-checks that this caller owns the award.
  updatePointTransaction(transactionId: number, points: number, reason: string, notes?: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/UpdatePointTransactionAsync`, {
      transactionId,
      points,
      reason,
      notes: notes ?? null
    });
  }

  // SPARK Live quick award: one student, the caller as teacher. Resolves to the transaction id.
  // sectionId wakes that classroom's live screen (0 when not awarding from live view or the remote).
  quickAwardPoints(schoolId: number, studentId: number, points: number, reason: string, notes: string | null, sectionId: number = 0): Observable<number> {
    return this.http.post<number>(`${this.baseApiUrl}/SchoolRewards/QuickAwardPointsAsync`, { schoolId, studentId, sectionId, points, reason, notes });
  }

  // SPARK Live: award a whole group in one tap; resolves to the ids needed to undo it as one unit.
  quickAwardGroup(request: { GroupId: number; SectionId: number; SchoolId: number; AcademicYear: number; StudentIds: number[];
                             Points: number; Reason: string; Notes: string | null }): Observable<QuickAwardGroupResult> {
    return this.http.post<QuickAwardGroupResult>(`${this.baseApiUrl}/SchoolRewards/QuickAwardGroupAsync`, request);
  }

  // SPARK Live: award several students (the whole class) in one tap; resolves to the transaction ids.
  quickAwardStudents(request: { SchoolId: number; SectionId: number; StudentIds: number[]; Points: number; Reason: string; Notes: string | null }): Observable<number[]> {
    return this.http.post<number[]>(`${this.baseApiUrl}/SchoolRewards/QuickAwardStudentsAsync`, request);
  }

  // Undoes a live tap: soft-deletes the listed transactions and, when set, drops the group award row.
  undoGroupAward(groupAwardId: number, transactionIds: number[], sectionId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/UndoGroupAwardAsync`, { groupAwardId, transactionIds, sectionId });
  }

  // Soft-deletes an award. The reason is required and is shown to the student and their guardians.
  deletePointTransaction(transactionId: number, deletionReason: string, sectionId: number = 0): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/DeletePointTransactionAsync`, {
      transactionId,
      deletionReason,
      sectionId
    });
  }

  // ---- Daily absences (date = the classroom's local day, yyyy-MM-dd) ----
  getSparkAbsentStudentIds(sectionId: number, date: string): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiUrl}/GetSparkAbsentStudentIdsAsync/${sectionId}/${date}`);
  }

  setSparkStudentAbsent(sectionId: number, studentId: number, date: string, absent: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/SetSparkStudentAbsentAsync`, { SectionId: sectionId, StudentId: studentId, Date: date, Absent: absent });
  }

  // ---- Live view sync ----
  sparkLiveHeartbeat(sectionId: number, schoolId: number): Observable<SparkLiveState> {
    return this.http.post<SparkLiveState>(`${this.apiUrl}/SparkLiveHeartbeatAsync/${sectionId}/${schoolId}`, null);
  }

  getSparkLiveState(sectionId: number, schoolId: number): Observable<SparkLiveState> {
    return this.http.get<SparkLiveState>(`${this.apiUrl}/GetSparkLiveStateAsync/${sectionId}/${schoolId}`);
  }

  getSparkLiveAwardsSince(sectionId: number, schoolId: number, sinceId: number, sinceUtc: string): Observable<SparkLiveAwards> {
    return this.http.get<SparkLiveAwards>(`${this.apiUrl}/GetSparkLiveAwardsSinceAsync/${sectionId}/${schoolId}/${sinceId}`,
                                          { params: { sinceUtc } });
  }

  // Begin/end dates of the school's academic year, for the "School Year" report preset.
  getAcademicYearRange(schoolId: number, academicYear: number): Observable<AcademicYearDateRange> {
    return this.http.get<AcademicYearDateRange>(`${this.baseApiUrl}/SchoolRewards/GetAcademicYearRangeAsync/${schoolId}/${academicYear}`);
  }

  saveSchoolRewardsConfig(config: SchoolRewardsConfig): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/SaveConfigAsync`, config);
  }

  // School Rewards Data Retrieval
  getSchoolRewardsStudentBalance(schoolId: number, studentId: number, academicYear: number): Observable<any> {
    return this.http.get<any>(`${this.baseApiUrl}/SchoolRewards/GetStudentBalanceAsync/${schoolId}/${studentId}/${academicYear}`);
  }

  getSchoolRewards(schoolId: number, pageNumber: number, pageSize: number, includeInactive: boolean, includeHidden: boolean = true, excludeOutOfStock: boolean = false): Observable<any> {
    return this.http.get<any>(`${this.baseApiUrl}/SchoolRewards/GetRewardsBySchoolAsync/${schoolId}?pageNumber=${pageNumber}&pageSize=${pageSize}&includeInactive=${includeInactive}&includeHidden=${includeHidden}&excludeOutOfStock=${excludeOutOfStock}`);
  }

  getSchoolReward(rewardId: number): Observable<SchoolReward> {
    return this.http.get<SchoolReward>(`${this.baseApiUrl}/SchoolRewards/GetRewardAsync/${rewardId}`);
  }

  addSchoolReward(reward: any): Observable<number> {
    return this.http.post<number>(`${this.baseApiUrl}/SchoolRewards/CreateRewardAsync`, reward);
  }

  updateSchoolReward(reward: any): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/UpdateRewardAsync/${reward.Id}`, reward);
  }

  processSchoolRewardsPurchase(studentId: number, schoolId: number, academicYear: number, rewardId: number, quantity: number, selectedSize?: string, selectedColor?: string, notes?: string): Observable<any> {
    const body = {
      StudentId: studentId,
      SchoolId: schoolId,
      AcademicYear: academicYear,
      Reward: {
        RewardId: rewardId,
        Quantity: quantity,
        SelectedSize: selectedSize,
        SelectedColor: selectedColor
      },
      Notes: notes
    };
    return this.http.post<any>(`${this.baseApiUrl}/SchoolRewards/ProcessPurchaseAsync`, body);
  }

  addSchoolRewardToWishList(studentId: number, schoolRewardId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/AddToWishListAsync/${studentId}/${schoolRewardId}`, {});
  }

  removeSchoolRewardFromWishList(studentId: number, schoolRewardId: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseApiUrl}/SchoolRewards/RemoveFromWishListAsync/${studentId}/${schoolRewardId}`);
  }

  // Spark (legacy) rewards live in a separate wishlist table from School Rewards.
  addRewardToWishList(studentId: number, rewardId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/AddRewardToWishListAsync/${studentId}/${rewardId}`, {});
  }

  removeRewardFromWishList(studentId: number, rewardId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/RemoveRewardFromWishListAsync/${studentId}/${rewardId}`, {});
  }

  // School Rewards Raffles
  addSchoolRewardsRaffle(raffle: SchoolRewardsRaffle): Observable<number> {
    return this.http.post<number>(`${this.baseApiUrl}/SchoolRewards/AddRaffleAsync`, raffle);
  }

  updateSchoolRewardsRaffle(raffle: SchoolRewardsRaffle): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/UpdateRaffleAsync`, raffle);
  }

  getSchoolRewardsRaffle(raffleId: number): Observable<SchoolRewardsRaffle> {
    return this.http.get<SchoolRewardsRaffle>(`${this.baseApiUrl}/SchoolRewards/GetRaffleAsync/${raffleId}`);
  }

  getSchoolRewardsRaffles(academicYear: number, schoolId: number): Observable<SchoolRewardsRaffle[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('academicYear', academicYear.toString());
    params = params.set('schoolId', schoolId.toString());

    return this.http.get<SchoolRewardsRaffle[]>(`${this.baseApiUrl}/SchoolRewards/GetRafflesAsync`, {
      params
    });
  }

  enterSchoolRewardsRaffle(raffleEntry: SchoolRewardsRaffleEntry): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/EnterRaffleAsync`, raffleEntry);
  }

  conductSchoolRewardsRaffleDrawing(raffleId: number): Observable<SchoolRewardsRaffleEntry[]> {
    return this.http.post<SchoolRewardsRaffleEntry[]>(`${this.baseApiUrl}/SchoolRewards/ConductRaffleDrawingAsync/${raffleId}`, {});
  }

  deleteSchoolRewardsRaffle(raffleId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/DeleteRaffleAsync/${raffleId}`, {});
  }

  updateSchoolRewardsShowResults(raffleId: number, studentId: number, showResults: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseApiUrl}/SchoolRewards/UpdateShowRaffleResultsAsync/${raffleId}/${studentId}/${showResults}`, {});
  }

  // Spark Award Points (Service Center admin; logs RewardsPointTransactions + RewardsStudentPointsSummaries)
  awardSparkPointsToStudents(schoolId: number, studentIds: number[], points: number, academicYear: number, reason: string, notes?: string): Observable<boolean> {
    const body = {
      schoolId,
      studentIds,
      points,
      academicYear,
      reason,
      ...(notes !== null && notes !== undefined && { notes })
    };
    return this.http.post<boolean>(`${this.apiUrl}/AwardPointsToStudentsAsync`, body);
  }

  // Award points to every student in every SPARK-enabled school. Returns the number of students awarded.
  awardSparkPointsToAllSchools(points: number, reason: string, notes?: string): Observable<number> {
    const body = {
      points,
      reason,
      ...(notes !== null && notes !== undefined && { notes })
    };
    return this.http.post<number>(`${this.apiUrl}/AwardPointsToAllSchoolsAsync`, body);
  }

  // Audit log of point awards made from the SparkAdmin view (for the Reports tab).
  getSparkPointAwardLog(): Observable<SparkPointAwardLogDto[]> {
    return this.http.get<SparkPointAwardLogDto[]>(`${this.apiUrl}/GetPointAwardLogAsync`);
  }

  // Spark Raffles (Service Center admin managed; backed by dbo.SparkRaffles)
  getSparkEnabledSchoolIds(): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiUrl}/GetSparkEnabledSchoolIdsAsync`);
  }

  addRaffle(raffle: SparkRaffle): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/AddRaffleAsync`, raffle);
  }

  updateRaffle(raffle: SparkRaffle): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/UpdateRaffleAsync`, raffle);
  }

  deleteRaffle(raffleId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/DeleteRaffleAsync/${raffleId}`, {});
  }

  getRaffle(raffleId: number): Observable<SparkRaffle> {
    return this.http.get<SparkRaffle>(`${this.apiUrl}/GetRaffleAsync/${raffleId}`);
  }

  getRaffles(academicYear: number, schoolId: number, onlyVisible: boolean = false): Observable<SparkRaffle[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('academicYear', academicYear.toString());
    params = params.set('schoolId', schoolId.toString());
    params = params.set('onlyVisible', onlyVisible.toString());

    return this.http.get<SparkRaffle[]>(`${this.apiUrl}/GetRafflesAsync`, {
      params
    });
  }

  conductRaffleDrawing(raffleId: number): Observable<SparkRaffleEntry[]> {
    return this.http.post<SparkRaffleEntry[]>(`${this.apiUrl}/ConductRaffleDrawingAsync/${raffleId}`, {});
  }

  enterRaffle(raffleEntry: SparkRaffleEntry): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/EnterRaffleAsync`, raffleEntry);
  }

  updateShowResults(raffleId: number, studentId: number, showResults: boolean): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/UpdateShowResultsAsync/${raffleId}/${studentId}/${showResults}`, {});
  }

}
